import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { AppState } from '../../store/state/app.state'
import { Store } from '@ngrx/store'
import { getSelectedCompaniesFromState, getUserFromState } from '../../store/selector/initialize.selector'
import { SettingsService } from '../../services/settings/settings.service'
import { getCommunicationPreferencesFromSettings } from '../../store/selector/settings.selector'
import { getEnabledModules } from '../../store/selector/company-settings.selector'
import { GlobalConstants } from '../../constants/global-constants'
import { CommonService } from 'src/app/services/common/common.service'
import { UserAPIModel } from 'src/app/models/api.model'
import { Subscription } from 'rxjs'
import { Router } from '@angular/router'

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit {
  communicationForm: FormGroup
  moduleForm: FormGroup
  referenceId: string
  referenceType: string
  private readonly subscriptions$: Subscription = new Subscription()
  private user: UserAPIModel

  readonly COMPANY_SETTINGS_TYPE = GlobalConstants.COMPANY_SETTINGS_TYPE
  readonly WHATSAPP = GlobalConstants.NOTIFICATION_PREFERENCES.WHATSAPP.toLowerCase()
  readonly SMS = GlobalConstants.NOTIFICATION_PREFERENCES.SMS.toLowerCase()
  readonly EMAIL = GlobalConstants.NOTIFICATION_PREFERENCES.EMAIL.toLowerCase()
  readonly ACTIVITY_TRACKER = GlobalConstants.MODULE.ACTIVITY_TRACKER.toLowerCase()
  readonly GOVERNMENT_BUSINESS_TASK = GlobalConstants.MODULE.GOVERNMENT_BUSINESS_TASK.toLowerCase()
  readonly CALENDAR = GlobalConstants.MODULE.CALENDAR.toLowerCase()
  readonly CIRCULAR_AND_COMMUNICATION = GlobalConstants.MODULE.CIRCULAR_AND_COMMUNICATION.toLowerCase()
  readonly DASHBOARD = GlobalConstants.MODULE.DASHBOARD.toLowerCase()
  readonly KNOWLEDGE_BANK = GlobalConstants.MODULE.KNOWLEDGE_BANK.toLowerCase()
  readonly DOCUMENT_REPOSITORY = GlobalConstants.MODULE.DOCUMENT_REPOSITORY.toLowerCase()
  readonly QUERIES = GlobalConstants.MODULE.QUERIES.toLowerCase()
  readonly ENABLED = 'Enabled'
  readonly DISABLED = 'Disabled'

  selection: 'new' | 'existing' | null = null
  selectedCompany: number | null = null
  isRmsUser = false

  companies = [
    { id: 1, name: 'Company A' },
    { id: 2, name: 'Company B' },
    { id: 3, name: 'Company C' }
  ]

  constructor(
    private formBuilder: FormBuilder,
    private appState: Store<AppState>,
    private settingsService: SettingsService,
    private cs: CommonService,
    private route: Router
  ) {}

  ngOnInit() {
    this.buildCommunicationForm()
    this.buildModuleForm()
    this.subscriptions$.add(
      this.appState.select(getEnabledModules).subscribe((enabledModules: string[]) => {
        if (enabledModules) {
          this.updateFormControlsFromPreferences(this.moduleForm, enabledModules)
        }
      })
    )
    this.fetchReferenceAndDispatchFetchSettings()
    this.subscriptions$.add(
      this.appState.select(getUserFromState).subscribe((user: UserAPIModel) => {
        if (user) {
          this.user = user
          this.isRmsUser = user?.userType === 'RMS_ADMIN'
        }
      })
    )
  }

  buildCommunicationForm() {
    this.communicationForm = this.formBuilder.group({
      [this.EMAIL]: [{ value: true }],
      [this.SMS]: [{ value: false }],
      [this.WHATSAPP]: [{ value: false }]
    })
  }

  buildModuleForm() {
    this.moduleForm = this.formBuilder.group({
      [this.ACTIVITY_TRACKER]: [{ value: false }],
      [this.GOVERNMENT_BUSINESS_TASK]: [{ value: false }],
      [this.CALENDAR]: [{ value: false }],
      [this.CIRCULAR_AND_COMMUNICATION]: [{ value: false }],
      [this.DASHBOARD]: [{ value: false }],
      [this.KNOWLEDGE_BANK]: [{ value: false }],
      [this.DOCUMENT_REPOSITORY]: [{ value: false }],
      [this.QUERIES]: [{ value: false }]
    })
  }

  fetchReferenceAndDispatchFetchSettings() {
    this.appState.select(getSelectedCompaniesFromState)?.subscribe(selectedCompanies => {
      if (selectedCompanies?.length > 0) {
        this.referenceId = selectedCompanies[0].id
        this.referenceType = selectedCompanies[0].type
        this.settingsService.dispatchFetchCompanySettingPreferencesData(this.referenceId, this.referenceType)
        this.updateFormsBasedOnSettings()
      }
    })
  }

  updateFormsBasedOnSettings() {
    //communication form
    this.appState.select(getCommunicationPreferencesFromSettings())?.subscribe(communicationPreferences => {
      if (communicationPreferences?.length > 0) {
        this.updateFormControlsFromPreferences(this.communicationForm, communicationPreferences)
      }
    })
  }

  updateFormControlsFromPreferences(form: FormGroup, preferences: string[]): void {
    if (!form || !preferences) {
      return
    }
    const lowercasePreferences = preferences?.map(ele => ele.toLowerCase())
    Object.keys(form.controls).forEach(controlName => {
      const control = form.get(controlName)
      if (control) {
        control.setValue(lowercasePreferences.includes(controlName))
      }
    })
  }

  saveSettings(settingsType: string, form: FormGroup) {
    const preferences = Object.keys(form.controls)
      .filter(controlName => form.controls[controlName].value === true)
      .map(controlName => controlName.toUpperCase())

    const companySettings: any = {}

    if (settingsType === this.COMPANY_SETTINGS_TYPE.NOTIFICATION_PREFERENCES) {
      companySettings['notificationPreferences'] = preferences
    }

    if (settingsType === this.COMPANY_SETTINGS_TYPE.MODULES) {
      companySettings['modules'] = preferences
    }

    this.settingsService.dispatchUpdateSettings(this.referenceId, this.referenceType, settingsType, companySettings)
  }

  emailPreferenceSetting() {
    if (!this.communicationForm.get(this.EMAIL)?.value) {
      this.cs.showError(`You cannot disable the default communication mode`)
    }
    this.communicationForm.get(this.EMAIL)?.setValue(true)
  }

  smsPreferenceSetting() {
    if (!this.user?.mobileNumber && this.communicationForm.get(this.SMS)?.value) {
      this.cs.showError(`You cannot enable the SMS, Valid Mobile number not found in your profile`)
      this.communicationForm.get(this.SMS)?.setValue(false)
    }
  }

  whatsappPreferenceSetting() {
    if (!this.user?.mobileNumber && this.communicationForm.get(this.WHATSAPP)?.value) {
      this.cs.showError(`You cannot enable the WhatsApp, Valid Mobile number not found in your profile`)
      this.communicationForm.get(this.WHATSAPP)?.setValue(false)
    }
  }

  ngOnDestroy() {
    this.subscriptions$.unsubscribe()
  }

  selectMode(mode: 'new' | 'existing') {
    this.selection = mode

    if (mode === 'new') {
      this.selectedCompany = null
      this.proceed() // direct navigation
    }
  }

  proceed() {
    if (this.selection === 'new') {
      this.route.navigate(['/settings/onboarding-wizard'], {
        queryParams: { mode: 'new' }
      })
    }

    if (this.selection === 'existing' && this.selectedCompany) {
      this.route.navigate(['/settings/onboarding-wizard'], {
        queryParams: {
          mode: 'edit',
          companyId: this.selectedCompany
        }
      })
    }
  }

}